import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Plus,
  Cpu,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { machinesAPI } from "../api";
import clsx from "clsx";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";

const statusConfig = {
  operational: {
    label: "Operational",
    color: "bg-green-500",
    textColor: "text-green-700",
    glow: "shadow-[0_0_8px_rgba(16,185,129,0.4)]",
  },
  maintenance: {
    label: "In Maintenance",
    color: "bg-yellow-500",
    textColor: "text-yellow-700",
    glow: "shadow-[0_0_8px_rgba(234,179,8,0.4)]",
  },
  error: {
    label: "Error",
    color: "bg-red-500",
    textColor: "text-red-700",
    glow: "shadow-[0_0_8px_rgba(239,68,68,0.4)]",
  },
  offline: {
    label: "Offline",
    color: "bg-slate-400",
    textColor: "text-slate-600",
    glow: "",
  },
};

/**
 * Replacement for MachineCard: Renders a single machine as a list row.
 */
const MachineListItem = ({ machine, index }) => {
  const status = statusConfig[machine.status] || statusConfig.offline;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/machines/${machine.id}`}
        // Using a 12-column grid layout for the list row
        className="grid grid-cols-12 items-center p-4 text-sm border-b border-slate-100 transition-colors hover:bg-slate-50 group"
      >
        {/* Column 1: Machine Name & Model */}
        <div className="col-span-3 flex flex-col">
          <span className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors truncate">
            {machine.name}
          </span>
          <span className="text-xs text-slate-500">{machine.model}</span>
        </div>

        {/* Column 2: Status */}
        <div className="col-span-2 flex items-center gap-2">
          <div
            className={clsx(
              "w-2.5 h-2.5 rounded-full",
              status.color,
              status.glow,
            )}
          />
          <span className={clsx("font-medium", status.textColor)}>
            {status.label}
          </span>
        </div>

        {/* Column 3: Manufacturer */}
        <div className="col-span-2 text-slate-700 truncate">
          {machine.manufacturer || "N/A"}
        </div>

        {/* Column 4: Location */}
        <div className="col-span-3 text-slate-700 truncate">
          {machine.location_in_plant || "N/A"}
        </div>

        {/* Column 5: Alerts */}
        <div className="col-span-1">
          {machine.active_alerts > 0 ? (
            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-100 py-1 px-2 rounded-full w-fit">
              <AlertTriangle className="w-3 h-3" />
              {machine.active_alerts}
            </span>
          ) : (
            <span className="text-slate-500">-</span>
          )}
        </div>

        {/* Column 6: Action Chevron */}
        <div className="col-span-1 flex justify-end pr-2">
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
};

const Machines = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const { data: machines, isLoading } = useQuery({
    queryKey: ["machines", { search, status: statusFilter }],
    queryFn: () =>
      machinesAPI
        .getAll({ search, status: statusFilter || undefined })
        .then((res) => res.data.data),
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterRef]);

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setIsFilterOpen(false);
  };

  const selectedStatus = statusConfig[statusFilter] || {
    label: "All Status",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Machines</h1>
          <p className="text-slate-500">Manage and monitor your equipment</p>
        </div>
        <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
          <Link to="/machines/add" className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Add Machine
          </Link>
        </HasPermission>
      </div>

      {/* Control Bar (Search and Filters) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search machines..."
            className="input w-full h-10 pl-12"
          />
        </div>
        <div className="flex gap-2">
          <div ref={filterRef} className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-10"
            >
              <div className="flex items-center gap-2">
                {statusFilter && (
                  <div
                    className={clsx(
                      "w-2 h-2 rounded-full",
                      statusConfig[statusFilter].color,
                    )}
                  />
                )}
                <span>{selectedStatus.label}</span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 text-slate-500 transition-transform",
                  isFilterOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-full sm:w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => handleFilterChange("")}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        statusFilter === ""
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Status
                    </button>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => handleFilterChange(key)}
                        className={clsx(
                          "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                          statusFilter === key
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        <div
                          className={clsx(
                            "w-2 h-2 rounded-full mr-3",
                            config.color,
                          )}
                        />
                        {config.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* <button className="btn btn-secondary h-10">
            <Filter className="w-4 h-4" />
            More Filters
          </button> */}
        </div>
      </div>

      {/* Loading State (Skeletons adapted to list format) */}
      {isLoading ? (
        <div className="card overflow-hidden">
          {/* Skeletons mimicking the 12-column layout: 3-2-2-3-1-1 */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 items-center p-4 border-b border-slate-200 last:border-b-0"
            >
              {/* Machine Name/Model */}
              <div className="col-span-3">
                <div className="h-5 w-40 skeleton mb-1" />
                <div className="h-3 w-20 skeleton" />
              </div>
              {/* Status */}
              <div className="col-span-2">
                <div className="h-4 w-24 skeleton" />
              </div>
              {/* Manufacturer */}
              <div className="col-span-2">
                <div className="h-4 w-32 skeleton" />
              </div>
              {/* Location */}
              <div className="col-span-3">
                <div className="h-4 w-48 skeleton" />
              </div>
              {/* Alerts */}
              <div className="col-span-1">
                <div className="h-4 w-10 skeleton rounded-full" />
              </div>
              {/* Action */}
              <div className="col-span-1" />
            </div>
          ))}
        </div>
      ) : machines && machines.length > 0 ? (
        <div className="card overflow-hidden p-0">
          {/* Table Header */}
          <div className="grid grid-cols-12 items-center text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200 px-4 py-3">
            <div className="col-span-3">Machine</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Manufacturer</div>
            <div className="col-span-3">Location</div>
            <div className="col-span-1">Alerts</div>
            <div className="col-span-1 text-right"></div>
          </div>

          {/* List Body */}
          <div className="divide-y divide-slate-100">
            {machines.map((machine, index) => (
              <MachineListItem
                key={machine.id}
                machine={machine}
                index={index}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <Cpu className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No machines found
          </h3>
          <p className="text-slate-500 mb-4">
            {search || statusFilter
              ? "Try adjusting your filters"
              : "Get started by adding your first machine"}
          </p>
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <Link to="/machines/add" className="btn btn-primary inline-flex">
              <Plus className="w-4 h-4" />
              Add Machine
            </Link>
          </HasPermission>
        </div>
      )}
    </div>
  );
};

export default Machines;
